import React, { useEffect, useMemo, useRef } from 'react'
import { EditorContent, useEditor, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import FontFamily from '@tiptap/extension-font-family'
import { FontSize } from './fontSize'
import { LineHeight } from './lineHeight'
import { useFontRegistry } from '../../fonts/FontRegistryContext'
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Quote,
  Redo,
  Strikethrough,
  Type as FontFamilyIcon,
  Underline as UnderlineIcon,
  Undo,
} from 'lucide-react'
import * as styles from './styles.module.scss'

export type RichtextFieldProps = {
  field: { type: 'custom'; label?: string }
  value: string | undefined
  onChange: (next: string) => void
  id?: string
}

const ToolbarButton: React.FC<{
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title: string
  children: React.ReactNode
}> = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    aria-pressed={active}
    disabled={disabled}
    onMouseDown={e => e.preventDefault()}
    onClick={onClick}
    className={`${styles.btn} ${active ? styles.btnActive : ''}`}
  >
    {children}
  </button>
)

const FONT_SIZE_OPTIONS = [
  { label: 'Default', value: '' },
  { label: '12px', value: '12px' },
  { label: '14px', value: '14px' },
  { label: '16px', value: '16px' },
  { label: '18px', value: '18px' },
  { label: '20px', value: '20px' },
  { label: '24px', value: '24px' },
  { label: '28px', value: '28px' },
  { label: '32px', value: '32px' },
  { label: '36px', value: '36px' },
  { label: '48px', value: '48px' },
  { label: '64px', value: '64px' },
]

const SYSTEM_FONT_OPTIONS = [
  { label: 'Default', value: '' },
  { label: 'System Sans', value: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' },
  { label: 'System Serif', value: 'Georgia, Cambria, "Times New Roman", Times, serif' },
  { label: 'Monospace', value: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
  { label: 'Site Header', value: 'var(--font-header)' },
  { label: 'Site Copy', value: 'var(--font-copy)' },
]

const FontFamilyControl: React.FC<{ editor: Editor }> = ({ editor }) => {
  const { fonts } = useFontRegistry()
  const current = (editor.getAttributes('textStyle')?.fontFamily as string) || ''

  const registered = fonts
    .filter(f => !!f.name)
    .map(f => ({ label: `Custom: ${f.name}`, value: `"${f.name}"` }))

  return (
    <select
      className={styles.select}
      title="Font Family"
      value={current}
      onChange={e => {
        const v = e.target.value
        if (!v) editor.chain().focus().unsetFontFamily().run()
        else editor.chain().focus().setFontFamily(v).run()
      }}
    >
      {SYSTEM_FONT_OPTIONS.map(o => (
        <option key={o.label} value={o.value}>{o.label}</option>
      ))}
      {registered.length > 0 && (
        <optgroup label="Registered Fonts">
          {registered.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </optgroup>
      )}
    </select>
  )
}

const LINE_HEIGHT_OPTIONS = [
  { label: 'Line: Default', value: '' },
  { label: 'Line: 1', value: '1' },
  { label: 'Line: 1.15', value: '1.15' },
  { label: 'Line: 1.25', value: '1.25' },
  { label: 'Line: 1.5', value: '1.5' },
  { label: 'Line: 1.75', value: '1.75' },
  { label: 'Line: 2', value: '2' },
  { label: 'Line: 2.5', value: '2.5' },
  { label: 'Line: 3', value: '3' },
]

const LineHeightControl: React.FC<{ editor: Editor }> = ({ editor }) => {
  const blocks = ['paragraph', 'heading'] as const
  const active = blocks.find(t => editor.isActive(t))
  const current = active
    ? (editor.getAttributes(active)?.lineHeight as string) || ''
    : ''
  return (
    <select
      className={styles.select}
      title="Line Height"
      value={current}
      onChange={e => {
        const v = e.target.value
        if (!v) editor.chain().focus().unsetLineHeight().run()
        else editor.chain().focus().setLineHeight(v).run()
      }}
    >
      {LINE_HEIGHT_OPTIONS.map(o => (
        <option key={o.label} value={o.value}>{o.label}</option>
      ))}
    </select>
  )
}

const FontSizeControl: React.FC<{ editor: Editor }> = ({ editor }) => {
  const current = (editor.getAttributes('textStyle')?.fontSize as string) || ''
  return (
    <select
      className={styles.select}
      title="Font Size"
      value={current}
      onChange={e => {
        const v = e.target.value
        if (!v) editor.chain().focus().unsetFontSize().run()
        else editor.chain().focus().setFontSize(v).run()
      }}
    >
      {FONT_SIZE_OPTIONS.map(o => (
        <option key={o.label} value={o.value}>{o.label}</option>
      ))}
    </select>
  )
}

const ColorControl: React.FC<{ editor: Editor }> = ({ editor }) => {
  const current = (editor.getAttributes('textStyle')?.color as string) || '#000000'
  return (
    <label className={styles.colorWrap} title="Text Color">
      <input
        type="color"
        className={styles.color}
        value={current}
        onChange={e => editor.chain().focus().setColor(e.target.value).run()}
      />
      <button
        type="button"
        className={styles.btn}
        title="Clear color"
        onMouseDown={e => e.preventDefault()}
        onClick={() => editor.chain().focus().unsetColor().run()}
      >
        ×
      </button>
    </label>
  )
}

const BlockSelect: React.FC<{ editor: Editor }> = ({ editor }) => {
  const current = (() => {
    if (editor.isActive('heading', { level: 1 })) return 'h1'
    if (editor.isActive('heading', { level: 2 })) return 'h2'
    if (editor.isActive('heading', { level: 3 })) return 'h3'
    if (editor.isActive('codeBlock')) return 'codeBlock'
    return 'paragraph'
  })()

  return (
    <select
      className={styles.select}
      value={current}
      onChange={e => {
        const v = e.target.value
        const chain = editor.chain().focus()
        if (v === 'paragraph') chain.setParagraph().run()
        else if (v === 'h1') chain.toggleHeading({ level: 1 }).run()
        else if (v === 'h2') chain.toggleHeading({ level: 2 }).run()
        else if (v === 'h3') chain.toggleHeading({ level: 3 }).run()
        else if (v === 'codeBlock') chain.toggleCodeBlock().run()
      }}
    >
      <option value="paragraph">Paragraph</option>
      <option value="h1">Heading 1</option>
      <option value="h2">Heading 2</option>
      <option value="h3">Heading 3</option>
      <option value="codeBlock">Code Block</option>
    </select>
  )
}

const Toolbar: React.FC<{ editor: Editor }> = ({ editor }) => {
  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined
    const next = window.prompt('URL (leave empty to remove):', prev || '')
    if (next === null) return
    if (next === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: next }).run()
  }

  return (
    <div className={styles.toolbar} role="toolbar">
      <BlockSelect editor={editor} />
      <FontFamilyControl editor={editor} />
      <FontSizeControl editor={editor} />
      <LineHeightControl editor={editor} />
      <ColorControl editor={editor} />

      <span className={styles.divider} />

      <ToolbarButton
        title="Bold"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Strikethrough"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Inline code"
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Code size={14} />
      </ToolbarButton>

      <span className={styles.divider} />

      <ToolbarButton
        title="Bullet list"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Ordered list"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Blockquote"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Horizontal rule"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <Minus size={14} />
      </ToolbarButton>

      <span className={styles.divider} />

      <ToolbarButton
        title="Align left"
        active={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        <AlignLeft size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Align center"
        active={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        <AlignCenter size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Align right"
        active={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        <AlignRight size={14} />
      </ToolbarButton>

      <span className={styles.divider} />

      <ToolbarButton
        title="Link"
        active={editor.isActive('link')}
        onClick={setLink}
      >
        <LinkIcon size={14} />
      </ToolbarButton>

      <span className={styles.divider} />

      <ToolbarButton
        title="Undo"
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Redo"
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo size={14} />
      </ToolbarButton>
    </div>
  )
}

export const RichtextField: React.FC<RichtextFieldProps> = ({
  field,
  value,
  onChange,
  id,
}) => {
  const extensions = useMemo(
    () => [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      LineHeight,
    ],
    [],
  )

  const lastEmittedRef = useRef<string>(value || '')

  const editor = useEditor({
    extensions,
    content: value || '',
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML()
      lastEmittedRef.current = html
      onChange(html)
    },
    editorProps: {
      attributes: {
        class: styles.editor,
        role: 'textbox',
        'aria-multiline': 'true',
        ...(id ? { id } : {}),
      },
    },
  })


  const stop = (e: React.SyntheticEvent) => e.stopPropagation()

  return (
    <div className={styles.wrapper}>
      <div className={styles.label}>{field.label || 'Rich Text'}</div>
      <div
        className={styles.root}
        onMouseDown={stop}
        onPointerDown={stop}
        onClick={stop}
        onFocus={stop}
      >
        <EditorContent editor={editor} />
        {editor && <Toolbar editor={editor} />}
      </div>
    </div>
  )
}

export default RichtextField
